import React, { useCallback, useState, useEffect } from 'react';
import SearchBar from '../SearchBar';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import { ModalForm, Option, CPOption } from './interface';
import { GET_B2C_USER_FILTERED, GET_ROLES, GET_ALL_CUSTOMERSLIST } from '../../graphql/client/queries';
import {
    GetRoles,
    GetRoles_getRoles as Role,
    GetAllCustomerList_getAllCustomerList as CustomerParent,
    GetAllCustomerList_getAllCustomerList as Customer,
    GetB2CUserFiltered_getB2CUserFiltered as B2CUser,
    GetB2CUserFiltered,
    GetAllCustomerList,
} from '../../types/schemaTypes';
import UserList from './UserList';
import { Error, Loading } from '../_common';
import AddUserModal from './AddEditUserModal/AddEditUserModal';
import './Users.scss';
import 'material-design-icons/iconfont/material-icons.css';
import { useQuery, useMutation } from '@apollo/client/react';
import { useStoreContext } from '../App/StoreProvider';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { DELETE_B2C_USER } from '../../graphql/client/mutations';
import { toast } from 'react-toastify';
import ConfirmDispatchModal from './ComfirmModal/DispatchModal';
import ForbiddenError from '../_common/Error/ForbiddenError';

const Messages = {
    delete: 'User deleted successfully',
    userAdded: 'User added successfully',
    userEdited: 'User details saved successfully',
};
const ErrorMessages = {
    delete: 'Failed to delete User',
};
let selectedUser: B2CUser | undefined;

const PAGE_SIZE = 50;

const Users = (): React.JSX.Element => {
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState(0);
    const [_tabTitle, _setTabTitle] = useState(document.title);
    const [isResetPassword, setIsResetPassword] = useState(false);
    const [showDispatchModal, setDispatchModal] = useState(false);
    document.title = 'Users';
    const isAdmin = useAuthContext().userHasPermission(PermissionEnum.Admin);
    const isCustomerService = useAuthContext().userHasPermission(PermissionEnum.CustomerService);
    const canEditUsers = isAdmin || isCustomerService;
    const [refetched, setRefetched] = useState(false);
    const [state, _dispatch] = useStoreContext();

    const {
        userList: { searchPhrase },
    } = state;

    const variables = {
        input: {
            searchString: searchPhrase,
            customerId: selectedCustomerId,
        },
    };

    const onCompleted = action => {
        toast.success(Messages[action]);
    };

    const onActionError = action => {
        toast.error(ErrorMessages[action]);
    };

    const checkIfPassword = () => {
        if (isResetPassword) {
            setDispatchModal(true);
        }
    };

    const handleClickHide = (): void => {
        setDispatchModal(false);
        setIsResetPassword(false);
    };

    const handleOnHide = (): void => {
        selectedUser = undefined;
        setErrorMessage('');
        setShowAddUserModal(false);
    };

    const onUserSaved = () => {
        !isResetPassword && onCompleted(selectedUser ? 'userEdited' : 'userAdded');
        selectedUser = undefined;
        setErrorMessage('');
        setShowAddUserModal(false);
        setRefetched(true);
    };

    const [deleteUser] = useMutation(DELETE_B2C_USER, {
        onCompleted: () => {
            onCompleted('delete');
            setRefetched(true);
        },
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_B2C_USER_FILTERED,
                    variables: { variables },
                },
            ];
        },
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { userId: record.userId } };
        await deleteUser({ variables });
    }, [deleteUser]);

    const input = {
        search: '',
        customerId: 0,
    };
    const { data: customerParentData } = useQuery<GetAllCustomerList>(GET_ALL_CUSTOMERSLIST, { variables: { input } });
    const cpOptions: CPOption[] = [];
    if (customerParentData?.getAllCustomerList?.length) {
        customerParentData.getAllCustomerList.forEach(element => {
            if (element && element.id !== null) {
                const id = element.id;
                const displayName = element.customerName ? element.customerName : '<None>';
                const jdeId = element.id ? element.id.toString() : '<None>';
                cpOptions.push({
                    id,
                    name: displayName,
                    jdeId,
                });
            }
        });
    }

    const { data: rolesData } = useQuery<GetRoles>(GET_ROLES);

    const options: Option[] = [];

    if (rolesData?.getRoles?.length) {
        rolesData.getRoles.forEach(element => {
            if (element && element.roleId !== null) {
                const id = element.roleId;
                const displayName = element.roleName ? element.roleName : '<None>';

                options.push({
                    id,
                    name: displayName,
                });
            }
        });
    }
    document.title = 'Users';

    const inputCustomerList = {
        search: '',
        customerId: 0,
    };

    const inputVariable = {
        input: inputCustomerList,
    };

    const { error: usersError, data: usersData, loading: usersLoading, refetch, fetchMore } = useQuery<GetB2CUserFiltered>(
        GET_B2C_USER_FILTERED,
        { variables, fetchPolicy: 'network-only' }
    );
    const { data: customersData } = useQuery<GetAllCustomerList>(GET_ALL_CUSTOMERSLIST, {
        variables: inputVariable,
        fetchPolicy: 'network-only',
    });

    const users: B2CUser[] = [];
    if (usersData?.getB2CUserFiltered?.length) {
        usersData.getB2CUserFiltered.forEach((element: B2CUser | null): void => {
            if (element !== null) users.push(element);
        });
    }
    const offset = usersData?.getB2CUserFiltered ? usersData.getB2CUserFiltered.length / PAGE_SIZE : 0;
    useEffect(() => {
        if (
            offset % 1 !== 0 ||
            (!isEndOfResults && usersData?.getB2CUserFiltered && usersData.getB2CUserFiltered.length < PAGE_SIZE)
        ) {
            setIsEndOfResults(true);
        }
    }, [offset, isEndOfResults, usersData]);

    if (!canEditUsers && !isCustomerService) {
        return <ForbiddenError />;
    }

    const onSelectCustomer = e => {
        setSelectedCustomerId(Number(e.target.value));
    };

    const customerUsers: Customer[] = [];
    if (customersData?.getAllCustomerList?.length) {
        customersData.getAllCustomerList.forEach((element: Customer | null): void => {
            if (element !== null) customerUsers.push(element);
        });
    }

    if (usersError) {
        return <Error error={usersError} />;
    }

    return (
        <div className="Users-Container">
            <div className="Users-Top-Container">
                <div className="Users-SearchBar">
                    <div className="pl-3">
                        <select
                            className="form-control"
                            onChange={e => onSelectCustomer(e)}
                            value={selectedCustomerId}
                        >
                            <option value={0}>-- Select Customer --</option>
                            {customerUsers.map((customer: any) => (
                                <option
                                    key={customer.id?.toString()}
                                    value={customer.id?.toString()}
                                >
                                    {customer.customerName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <SearchBar type={SearchTypeEnum.User}></SearchBar>
                </div>
                            <div className="Users-RightOptions">
                                {canEditUsers && (
                                    <div
                                        className="Users-Create-New-User Row"
                                        onClick={() => {
                                            setShowAddUserModal(true);
                                            errorMessage && setErrorMessage('');
                                            setModalForm(ModalForm.ADD_USER);
                                        }}
                                    >
                                        <i className="material-icons Users-Icon">add_circle</i>
                                        <span className="Users-Link">Create New User</span>
                                    </div>
                                )}
                            </div>
            </div>
            <div className="Users-Bottom-Container">
                            <div className="Users-Bottom-Header-Container">
                                <div className="Users-Header-Title">Name</div>
                                <div className="Users-Header-Title Users-Header-Title-Mid">Email</div>
                                <div className="Users-Header-Title">Role</div>
                                <div className="Users-Header-Title Users-Header-Title-Lg">Customer Parent</div>
                                <div className="Users-Header-Title Users-Header-Title-Mid">Customer Parent JDE ID</div>
                                <div className="Users-Header-Title">Status</div>
                                <div className="Users-Header-Title">Actions</div>
                            </div>
                            {usersLoading && (
                                <div className="SpinnerContainer">
                                    <Loading />
                                </div>
                            )}

                            {users.length > 0 && (
                                <UserList
                                    users={users}
                                    options={options}
                                    cpOptions={cpOptions}
                                    isLoading={usersLoading}
                                    canEditUsers={canEditUsers}
                                    refetch={refetch}
                                    refetched={refetched}
                                    setRefetched={setRefetched}
                                    isEndOfResults={isEndOfResults}
                                    onEditClick={user => {
                                        console.log('on edit user', user);
                                        setShowAddUserModal(true);
                                        errorMessage && setErrorMessage('');
                                        setModalForm(ModalForm.EDIT_USER);
                                        selectedUser = user;
                                    }}
                                    onDelete={onDelete}
                                    onLoadMore={() =>
                                        fetchMore({
                                            variables: { offset },
                                        })
                                    }
                                />
                            )}
                            <AddUserModal
                                show={showAddUserModal}
                                modalForm={modalForm}
                                options={options}
                                cpOptions={cpOptions}
                                selectedUser={selectedUser}
                                onClickHide={handleOnHide}
                                onUserSaved={onUserSaved}
                                checkIfPassword={checkIfPassword}
                                setIsResetPassword={setIsResetPassword}
                                errorMessage={errorMessage}
                                setErrorMessage={setErrorMessage}
                                userId={selectedUser?.userId == null ? 0 : selectedUser.userId}
                                inputVariables={variables.input}
                            />
                            <ConfirmDispatchModal
                                isResetPassword={true}
                                show={showDispatchModal}
                                onClickHide={handleClickHide}
                            />
            </div>
        </div>
    );
};

export default Users;
